import { DocumentsRecycleBinItem } from "client/Api/Documents/SharePoint/common";
import { deleteRecycledItems } from "client/Api/Documents/SharePoint/sharePointRecycleBin";
import {
	BusyIndicator,
	FormattedText,
	Portal,
	useConfirmationDialog
} from "client/Components";
import { AlertManager } from "client/Hooks";
import { convertToLocationModel } from "client_legacy/Documents/SharePoint/listLocationModelProvider";
import { localize } from "client_legacy/ndaw/culture";
import { Guid } from "client_legacy/ndaw/guid";
import {
	ReactElement,
	useCallback,
	useState
} from "react";

export function useDeleteFromRecyclebin(
	onDeleted: (ids: readonly Guid[]) => void,
	alertManager: AlertManager
): [
	element: ReactElement | null,
	invoke: (documents: readonly DocumentsRecycleBinItem[]) => Promise<void>
] {
	const [confirmationDialog, openConfirmationDialog] = useConfirmationDialog();
	const [isBusy, setIsBusy] = useState<boolean>(false);

	const invoke = useCallback(
		async (documents: readonly DocumentsRecycleBinItem[]): Promise<void> => {
			let body: ReactElement;
			if (documents.length > 1) {
				body = (
					<FormattedText
						text={localize("_AreYouSureYouWantToDeleteAmountItemsPermanently")}
						thingsToReplace={[
							{ what: "{{Amount}}", filler: documents.length }
						]}
					/>
				);
			} else {
				body = (
					<FormattedText
						text={localize("_AreYouSureYouWantToDeleteNamePermanently")}
						thingsToReplace={[
							{ what: "{{Name}}", filler: documents[0].name }
						]}
					/>
				);
			}

			const result: TryResult<void> = await openConfirmationDialog({
				title: localize("_DeletePermanently"),
				children: body,
				confirmText: localize("_Delete"),
				trackingId: "delete-files-permanently-confirmation-dialog"
			});

			if (!result.wasSuccessful) {
				return;
			}

			const deletedItemIds: Guid[] = documents.map((x) => x.guid);

			setIsBusy(true);

			try {
				await deleteRecycledItems({
					location: convertToLocationModel(
						documents[0].location
					),
					deletedItems: deletedItemIds
				});

				onDeleted(deletedItemIds);

				alertManager.showSuccess(localize("_FilesDeletedSuccessfully"));
			} catch (e) {
				alertManager.showError(localize("_GeneralErrorMessage"));
				console.error(e);
			} finally {
				setIsBusy(false);
			}
		},
		[
			alertManager,
			onDeleted,
			openConfirmationDialog,
			setIsBusy
		]
	);

	const renderElement = (): ReactElement | null => {
		if (!confirmationDialog && !isBusy) {
			return null;
		}

		return (
			<>
				{isBusy && (
					<Portal container={document.body}>
						<BusyIndicator />
					</Portal>
				)}

				{confirmationDialog}
			</>
		);
	};

	return [renderElement(), invoke];
}